<template>
  <section class="mx-auto max-w-screen-xl" data-testid="games-slider">
    <div class="mb-2">
      <h1
        class="text-center uppercase"
        data-testid="homepage-heading-games-slider"
      >
        {{ data.headline }}
      </h1>

      <div
        class="mt-8 flex flex-wrap items-center justify-center gap-4 px-4 sm:justify-between"
      >
        <!-- Tabs -->
        <div class="flex items-center gap-4 lg:gap-8">
          <button
            v-for="tab in tabs"
            :key="tab.group"
            :aria-label="tab.label"
            :data-testid="
              tab.group === GamesSliderGroup.Popular
                ? 'homepage-games-tab-top'
                : 'homepage-games-tab-new'
            "
            class="group/tab flex items-center gap-1.5 text-lg font-bold uppercase text-rhino lg:gap-2 lg:text-2xl"
            @click="switchTab(tab.group)"
          >
            <common-svg-icon
              :icon="tab.icon"
              :alt="tab.label"
              :is-highlighted="activeGroup === tab.group"
              :width="24"
              :height="24"
              disable-hover
              class="shrink-0 group-hover/tab:[--gp-icon-fill:url(#gp-gradient)]"
            />
            <span class="relative text-mouse">
              {{ tab.label }}
              <span
                class="absolute -bottom-1 left-0 h-0.5 w-full rounded-md transition-opacity duration-300"
                :class="
                  activeGroup === tab.group
                    ? 'opacity-100'
                    : 'opacity-0 group-hover/tab:opacity-100'
                "
                :style="{
                  background:
                    'linear-gradient(to right, var(--gp-accent-gold), var(--gp-accent-magenta))',
                }"
              />
            </span>
          </button>
        </div>

        <!-- "To all servers" link -->
        <common-button :to="indexUrl" size="sm" class="shrink-0">
          {{
            t('homePage/to_all', {
              context: t('general/non_special_texts/server_plural'),
            })
          }}
        </common-button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="!hasLoaded && pending"
      class="mb-8 flex gap-4 overflow-hidden px-4"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="w-[215px] shrink-0 animate-pulse rounded-xl bg-depth-2"
        :style="{ aspectRatio: '215 / 305' }"
      />
    </div>

    <!-- Error state -->
    <div
      v-else-if="isError && products.length === 0"
      class="mb-8 flex flex-col items-center gap-4 py-12 text-center"
    >
      <p class="text-rhino">
        {{ t('general/error/text') }}
      </p>
      <common-button
        outline
        size="custom"
        class="px-3 py-1.5"
        @click="refresh()"
      >
        {{ t('general/retry') }}
      </common-button>
    </div>

    <!-- Carousel -->
    <card-slider v-else-if="products.length > 0" ref="carouselRef" class="mb-8">
      <div
        v-for="product in products"
        :key="product.id"
        class="h-[305px] w-[215px] shrink-0 snap-start 2xl:w-[18.75%]"
      >
        <games-slider-game-server-card :game="product" class="h-full w-full" />
      </div>
    </card-slider>
  </section>
</template>

<script setup lang="ts">
import {
  GetHomePageProducts,
  type GetHomePageProductsQuery,
  type GetHomePageProductsQueryVariables,
  type StrapiHomePage,
  type StrapiProduct,
} from '~/apollo/types/types';
import { GamesSliderGroup } from '~/components/games-slider/types';

const MrMaintenanceIcon = defineAsyncComponent(
  () => import('~/assets/icons/mr-maintenance.svg?component'),
);
const GamepadIcon = defineAsyncComponent(
  () => import('~/assets/icons/gamepad.svg?component'),
);

const router = useRouter();
const { t, locale } = useI18n();
const currencyStore = useCurrencyStore();
const authStore = useAuthStore();
const { scrollToTop } = useWindowScroll();

const props = defineProps<{
  data: StrapiHomePage;
}>();

const carouselRef = ref<{ trackEl: HTMLElement }>();
const activeGroup = ref<GamesSliderGroup>(GamesSliderGroup.Popular);

const tabs = computed(() => [
  {
    group: GamesSliderGroup.Popular,
    label: t('landingpage/index/top_games'),
    icon: MrMaintenanceIcon,
  },
  {
    group: GamesSliderGroup.New,
    label: t('homePage/newest_games'),
    icon: GamepadIcon,
  },
]);

const indexUrl = computed(() => {
  const route = router
    .getRoutes()
    .find((r) => r.name === `landingpage-gameserver-slug___${locale.value}`);

  if (route != null) {
    return route.path.replace('/:slug()', '/index');
  }

  return `/${locale.value}/gameserver/index`;
});

// Data fetching — tab switches call refresh() manually
const {
  refresh,
  data: fetchedProducts,
  status,
  error,
} = await useAsyncData(
  `gp-games-slider-${locale.value}-${currencyStore.currency}`,
  async () => {
    const { data: response, error: apolloError } = await useLegacyApollo<
      GetHomePageProductsQuery,
      GetHomePageProductsQueryVariables
    >(GetHomePageProducts, {
      locale: locale.value,
      currency: currencyStore.currency,
      group: activeGroup.value,
    });

    if (apolloError || !response?.products) {
      throw new Error('Failed to fetch games slider products');
    }

    const items = response.products as Array<StrapiProduct>;
    items.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1));

    return items.map((p) => ({
      ...p,
      id: `${activeGroup.value}__${p.id}`,
    }));
  },
);

const hasLoaded = ref(
  fetchedProducts.value != null && fetchedProducts.value.length > 0,
);
const pending = computed(() => status.value === 'pending');
const isError = computed(() => error.value != null);
const products = computed(() => fetchedProducts.value ?? []);

watch(products, async (val) => {
  if (val.length > 0) hasLoaded.value = true;
  await nextTick();
  carouselRef.value?.trackEl?.scrollTo({ left: 0, behavior: 'smooth' });
});

// Re-fetch on currency change
useUserCurrency(refresh, async () => {
  await refresh();
  scrollToTop();
});

// Re-fetch on region change
watch(
  () => authStore.activeRegion,
  async () => {
    await refresh();
    scrollToTop();
  },
);

function switchTab(group: GamesSliderGroup) {
  activeGroup.value = group;
  refresh();
}
</script>
